// MobileLayout — the < 1024px property-detail composition. Hero
// carousel with swipe + dots + counter, bottom-sheet style content
// panel with badges/title/price/freshness/specs/description/broker,
// sticky bottom action bar (edit pencil + Đặt lịch xem). The hero's
// top-right three-dot button opens `MobileMoreSheet` (history, AI,
// shares, delete) and the counter/image opens the shared
// `PropertyLightbox` in full-screen variant.

import { useState } from "react";
import {
  Calendar,
  Camera,
  ChevronLeft,
  MapPin,
  MoreVertical,
  Sliders,
} from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AuthImg } from "@/components/auth-img";
import { cn } from "@/lib/utils";
import { useSwipeNav } from "@/features/property/wizard/use-swipe-nav";
import { formatPrice } from "@/features/property/utils/format-price";
import { propertyTypeLabel } from "@/features/property/utils/property-enum-options";
import { pricePerSqmLabel } from "@/features/property/utils/property-display";
import { useCanManageProperty } from "@/features/property/hooks/use-can-manage-property";
import { useZaloShareHandler } from "@/features/property/hooks/use-zalo-share-handler";
import { usePublicShareHandler } from "@/features/property/hooks/use-public-share-handler";
import { PropertyFreshnessBanner } from "@/features/property/components/property-freshness-banner";
import PropertyQuickStatusButton from "@/features/property/components/property-quick-status-button";
import { MobileMoreSheet } from "@/features/property/components/mobile-more-sheet";
import { SpecsGrid } from "@/features/property/components/detail/specs-grid";
import { ProjectInfoCard } from "@/features/property/components/detail/project-info-card";
import { DescriptionBlock } from "@/features/property/components/detail/description-block";
import { BrokerCard } from "@/features/property/components/detail/broker-card";
import { PropertyLightbox } from "@/features/property/components/detail/property-lightbox";
import type { Property, PropertyImage } from "@/types/property";

interface MobileLayoutProps {
  property: Property;
  images: PropertyImage[];
  onBack: () => void;
  onOpenSchedule: () => void;
}

export function MobileLayout({
  property,
  images,
  onBack,
  onOpenSchedule,
}: MobileLayoutProps) {
  const title = property.buildingName ?? "Bất động sản";
  const canManage = useCanManageProperty(property);
  const pricePerSqm = pricePerSqmLabel(property);

  // Carousel state. activeIndex tracks which photo the hero currently
  // shows; swipe left = next, swipe right = previous. When the gallery is
  // empty we clamp to 0 so the placeholder still renders without going
  // out of bounds elsewhere.
  const [activeIndex, setActiveIndex] = useState(0);
  const total = images.length;
  const safeIndex = total === 0 ? 0 : Math.min(activeIndex, total - 1);
  const current = images[safeIndex];
  const dotCount = Math.min(5, Math.max(1, total || 1));
  const activeDotIndex = total > 0 ? safeIndex % dotCount : 0;

  const goNext = () => {
    if (total <= 1) return;
    setActiveIndex((i) => (i + 1) % total);
  };
  const goPrev = () => {
    if (total <= 1) return;
    setActiveIndex((i) => (i - 1 + total) % total);
  };
  const swipeHandlers = useSwipeNav({
    onSwipeLeft: goNext,
    onSwipeRight: goPrev,
  });

  // Lightbox — full-screen image viewer. Tapping the hero (or its
  // counter pill) opens it at the currently-visible photo so the user
  // doesn't lose context.
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // The More sheet owns the rest of the actions (history, AI, shares,
  // delete). We hold its open state here so we can drive the hero's
  // three-dot button. Share helpers are extracted so the sheet rows and
  // the desktop action grid call into the same code path.
  const [moreOpen, setMoreOpen] = useState(false);
  const zaloShare = useZaloShareHandler(property);
  const publicShare = usePublicShareHandler(property);

  // Preview shape mirrors what the existing PublicLinkTile shows — we
  // don't know the real token until the user clicks (the mutation issues
  // one), so the sheet row shows a generic origin/p/… stub.
  const origin = typeof window !== "undefined" ? window.location.origin : "estate.vn";
  const publicLinkPreview = `${origin.replace(/^https?:\/\//, "")}/p/…`;

  return (
    <div className="relative flex min-h-screen flex-col bg-background pb-[88px]">
      {/* Hero image carousel. Pointer events on the container drive the
          swipe-nav hook; the image itself sits inside a button so a tap
          (not a swipe) opens the lightbox. */}
      <div
        className="relative h-[320px] w-full touch-pan-y select-none overflow-hidden"
        {...swipeHandlers}
      >
        {current ? (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            aria-label="Xem ảnh phóng to"
            className="absolute inset-0 cursor-zoom-in"
          >
            <AuthImg
              src={current.fullUrl}
              alt={current.originalFilename || title}
              loading="eager"
              className="h-full w-full object-cover"
            />
          </button>
        ) : property.thumbnailUrl ? (
          // No uploaded photos yet → show the seeded placeholder (never stored;
          // a real upload replaces it) so the hero isn't an empty box.
          <img
            src={property.thumbnailUrl}
            alt=""
            loading="eager"
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            aria-label="Chưa có ảnh"
            className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground"
          >
            <Camera aria-hidden="true" className="size-10 opacity-40" />
          </div>
        )}
        {/* Gradient overlay fading into the page background near the bottom.
            `pointer-events-none` so it doesn't swallow taps + swipes. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.42)_0%,rgba(0,0,0,0)_25%,rgba(0,0,0,0)_70%,rgba(250,250,249,0.96)_100%)]"
        />

        {/* Back button (top-left). Above the hero button via z-index so
            its click goes here, not to the lightbox-opener underneath. */}
        <div className="absolute top-[14px] left-[14px] z-10">
          <button
            type="button"
            onClick={onBack}
            aria-label="Quay lại"
            className="flex size-[38px] items-center justify-center rounded-[12px] bg-white/90 text-foreground shadow-sm backdrop-blur-md hover:bg-white"
          >
            <ChevronLeft aria-hidden="true" className="size-[18px]" />
          </button>
        </div>

        {/* More button (top-right) — opens MobileMoreSheet with the rerouted
            action grid + history + delete entries. */}
        <div className="absolute top-[14px] right-[14px] z-10">
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-label="Tuỳ chọn"
            aria-haspopup="dialog"
            className="flex size-[38px] items-center justify-center rounded-[12px] bg-white/90 text-foreground shadow-sm backdrop-blur-md hover:bg-white"
          >
            <MoreVertical aria-hidden="true" className="size-[18px]" />
          </button>
        </div>

        {/* Image counter — also opens the lightbox. The hint text
            advertises the tap interaction. */}
        {total > 0 ? (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            aria-label="Xem ảnh phóng to"
            className="absolute right-[14px] bottom-[60px] z-10 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-[5px] font-mono text-[11px] text-white"
          >
            <Camera aria-hidden="true" className="size-[11px]" />
            {String(safeIndex + 1).padStart(2, "0")} / {total} · xem ảnh
          </button>
        ) : null}

        {/* dots — purely decorative, ignore pointer events so swipes still
            register on the hero. Active dot tracks the carousel index
            (modulo dotCount for galleries larger than 5). */}
        {total > 0 ? (
          <div className="pointer-events-none absolute bottom-[60px] left-[14px] flex gap-1">
            {Array.from({ length: dotCount }).map((_, i) => (
              <span
                key={i}
                aria-hidden
                className={cn(
                  "h-[5px] rounded-full transition-all",
                  i === activeDotIndex ? "w-4 bg-white" : "w-[5px] bg-white/50",
                )}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* Bottom sheet content */}
      <div className="relative -mt-10 flex-1 rounded-t-[20px] bg-background pb-6 shadow-[0_-8px_30px_rgba(0,0,0,0.06)]">
        {/* Drag handle */}
        <div className="mx-auto mt-2.5 h-1 w-9 rounded-full bg-border-strong" />

        <div className="flex flex-col gap-3.5 px-[18px] pt-3.5">
          {/* Badges row.
              Design order: internal code (mono) · QuickStatusButton (pill) ·
              propertyType accent badge (when present). The listing-type chip
              (Bán/Cho thuê) is intentionally dropped per the design — the
              status pill already implies the listing posture. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {property.internalCode !== null ? (
              <Badge variant="neutral" className="font-mono text-[11px]">
                {property.internalCode}
              </Badge>
            ) : null}
            <PropertyQuickStatusButton
              property={property}
              readOnly={!canManage}
            />
            {property.propertyType !== null ? (
              <Badge variant="info" className="text-[11px]">
                {propertyTypeLabel(property.propertyType)}
              </Badge>
            ) : null}
          </div>

          {/* Title */}
          <h1 className="text-[28px] leading-tight font-bold tracking-tight text-balance">
            {title}
          </h1>

          {/* Location */}
          <div className="inline-flex items-center gap-1.5 text-[14px] text-muted-foreground">
            <MapPin aria-hidden="true" className="size-[13px]" />
            <span>
              {property.wardDisplayName}
              {property.floor !== null ? ` · Tầng ${property.floor}` : ""}
            </span>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-2.5 border-t border-border pt-3.5">
            <span className="font-mono text-3xl leading-none font-bold tracking-tight tabular-nums">
              {formatPrice(property.price, property.listingType)}
            </span>
            {pricePerSqm !== null ? (
              <span className="font-mono text-[11px] text-muted-foreground">
                {pricePerSqm}
              </span>
            ) : null}
          </div>

          {/* Freshness banner */}
          <PropertyFreshnessBanner
            propertyId={property.id}
            lastFreshnessConfirmedAt={property.lastFreshnessConfirmedAt}
            canConfirm={canManage}
          />

          {/* Specs grid */}
          <SpecsGrid property={property} />

          {/* Project (building) info — only when the unit is linked. */}
          <ProjectInfoCard property={property} />

          {/* Description */}
          {property.description !== null && property.description.trim().length > 0 ? (
            <DescriptionBlock description={property.description} />
          ) : null}

          {/* Broker card. Standalone action grid (AI / Zalo / public link)
              and the inline activity expander used to live here on mobile —
              both are now reached through the More sheet (top-right) so
              the default scroll stays focused on listing facts. */}
          <BrokerCard property={property} />
        </div>
      </div>

      {/* Sticky bottom action bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-2 border-t border-border bg-card px-3.5 pt-2.5 pb-[22px]">
        {canManage ? (
          <Button
            asChild
            variant="outline"
            size="icon-lg"
            aria-label="Chỉnh sửa bất động sản"
            className="size-11 rounded-xl"
          >
            <Link to={`/properties/${property.id}/edit`}>
              <Sliders aria-hidden="true" className="size-4" />
            </Link>
          </Button>
        ) : null}
        <Button
          type="button"
          onClick={onOpenSchedule}
          className="h-11 flex-1 rounded-xl bg-foreground text-background hover:bg-foreground/90"
        >
          <Calendar aria-hidden="true" className="size-[15px]" />
          Yêu cầu xem nhà
        </Button>
      </div>

      {/* More sheet (hosts history / AI / shares / delete). */}
      <MobileMoreSheet
        property={property}
        open={moreOpen}
        onOpenChange={setMoreOpen}
        onShareZalo={() => void zaloShare.trigger()}
        onSharePublicLink={() => void publicShare.trigger()}
        publicLinkPreview={publicLinkPreview}
      />

      {/* Full-screen lightbox — opened by tapping the hero image or the
          counter pill. Swipe inside the lightbox cycles between photos
          (same handler the hero uses) and the prev/next arrows are kept
          for accessibility. */}
      <PropertyLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        images={images}
        index={safeIndex}
        onPrev={goPrev}
        onNext={goNext}
        fullScreen
      />
    </div>
  );
}
